import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { api } from "@/lib/api/client";
import { PageHeader } from "@/components/ui/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { Pagination, DEFAULT_PAGE_SIZE } from "@/components/ui/Pagination";
import { SearchPanel, filterInputClass } from "@/components/ui/SearchPanel";
import { StatusBadge } from "@/components/ui/StatusBadge";

/** Cột và trạng thái lấy theo bảng `meeting_minutes` + MeetingMinutesDAO của hệ cũ. */
export type MinutesItem = {
  MEETING_MINUTES_ID: number;
  TITLE: string | null;
  CODE: string | null;
  CONCLUSION_DATE: string | null;
  STATUS: number | null;
  TEXT_ID: number | null;
  FILE_ID: number | null;
  CREATED_DATE: string | null;
  CONCLUSION_NAME: string | null;
  CREATOR_NAME: string | null;
  ORG_CONCLUSION_NAME: string | null;
};

const STATUS_LABEL: Record<number, string> = {
  0: "Dự thảo",
  1: "Chờ ký",
  2: "Đã ký",
  3: "Đã ban hành",
};

const STATUS_VARIANT: Record<number, "neutral" | "warning" | "info" | "success"> = {
  0: "neutral",
  1: "warning",
  2: "info",
  3: "success",
};

type Filters = { q: string; status: string; fromDate: string; toDate: string };
const EMPTY: Filters = { q: "", status: "", fromDate: "", toDate: "" };

async function fetchMinutes(page: number, pageSize: number, f: Filters) {
  const { data } = await api.get("/meeting/minutes/list", {
    params: {
      page,
      pageSize: pageSize,
      q: f.q || undefined,
      status: f.status || undefined,
      fromDate: f.fromDate || undefined,
      toDate: f.toDate || undefined,
    },
  });
  return (data.data ?? data) as {
    items: MinutesItem[];
    total: number;
    page: number;
    pageSize: number;
  };
}

export function MeetingMinutesPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);

  const { data, isLoading } = useQuery({
    queryKey: ["meeting-minutes", page, pageSize, applied],
    queryFn: () => fetchMinutes(page, pageSize, applied),
  });

  const columns: Column<MinutesItem>[] = [
    {
      key: "stt",
      header: "#",
      width: "48px",
      align: "center",
      render: (_r, i) => (
        <span className="text-muted-foreground tabular-nums">{(page - 1) * pageSize + i + 1}</span>
      ),
    },
    { key: "code", header: "Số, ký hiệu", width: "150px", render: (r) => r.CODE ?? "—" },
    {
      key: "title",
      header: "Tiêu đề biên bản",
      render: (r) => <span className="font-medium">{r.TITLE ?? "—"}</span>,
    },
    {
      key: "conclusion",
      header: "Người kết luận",
      width: "180px",
      render: (r) => r.CONCLUSION_NAME ?? "—",
    },
    {
      key: "org",
      header: "Đơn vị kết luận",
      width: "200px",
      render: (r) => r.ORG_CONCLUSION_NAME ?? "—",
    },
    {
      key: "date",
      header: "Ngày kết luận",
      width: "130px",
      render: (r) => (
        <span className="text-sm tabular-nums">{r.CONCLUSION_DATE?.slice(0, 10) ?? "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: "130px",
      align: "center",
      render: (r) => (
        <StatusBadge variant={STATUS_VARIANT[r.STATUS ?? 0] ?? "neutral"}>
          {STATUS_LABEL[r.STATUS ?? 0] ?? `#${r.STATUS}`}
        </StatusBadge>
      ),
    },
    {
      key: "text",
      header: "Văn bản ký",
      width: "110px",
      align: "center",
      render: (r) =>
        r.TEXT_ID ? (
          <a className="text-primary underline" href={`/signs/${r.TEXT_ID}`}>
            #{r.TEXT_ID}
          </a>
        ) : (
          "—"
        ),
    },
  ];

  const submit = () => {
    setApplied(filters);
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Biên bản họp"
        subtitle={`${(data?.total ?? 0).toLocaleString("vi-VN")} biên bản`}
        breadcrumb="Quản lý lịch họp › Biên bản"
      />
      <SearchPanel
        fields={[
          {
            label: "Từ khóa",
            control: (
              <input
                className={filterInputClass}
                placeholder="Tiêu đề, số ký hiệu…"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              />
            ),
          },
          {
            label: "Trạng thái",
            control: (
              <select
                className={filterInputClass}
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">---- Tất cả ----</option>
                {Object.entries(STATUS_LABEL).map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            label: "Từ ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.fromDate}
                onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
              />
            ),
          },
          {
            label: "Đến ngày",
            control: (
              <input
                type="date"
                className={filterInputClass}
                value={filters.toDate}
                onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
              />
            ),
          },
        ]}
        onSubmit={submit}
      />
      <DataTable<MinutesItem>
        columns={columns}
        rows={data?.items ?? []}
        keyBy={(r) => r.MEETING_MINUTES_ID}
        isLoading={isLoading}
        empty={
          <>
            <FileText className="inline h-4 w-4 mr-1" /> Chưa có biên bản họp nào — hệ cũ cũng đang
            trống (bảng <code>meeting_minutes</code> = 0 bản ghi).
          </>
        }
        dense
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
      />
    </div>
  );
}
